import { useMemo, useState } from "react"
import { Loader2 } from "lucide-react"
import type { AppShell } from "@/App"
import { errorMessage, moveOrder, type MoveImpact, type MoveTarget, type Order, type PlanView, type Vehicle } from "@/lib/api"
import { day10, dayShort, eur, hhmm, kg as fmtKg, n } from "@/lib/format"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Tag } from "@/components/kit"
import { cn } from "@/lib/utils"

interface Option {
  key: string
  target: MoveTarget
  label: string
  sub: string
  warnings: string[]
  disabled: string | null
}

const signed = (v: number) => `${v > 0 ? "+" : v < 0 ? "−" : ""}${eur(Math.abs(v))}`

/**
 * «Atribuir…» / «Mover…» (D4): escolhe o destino — veículo × dia (hoje e o
 * horizonte) ou uma transportadora concreta — com os avisos à vista, mostra o
 * impacto calculado pelo serviço de rotas (km, volta, €, problemas) e só grava
 * em «Aplicar». Na frota a encomenda fica fixada; a transportadora escolhida à
 * mão fica na encomenda.
 */
export function MoveDialog({
  shell,
  order,
  view,
  vehicles,
  onClose,
  onApplied,
}: {
  shell: AppShell
  order: Order
  view: PlanView
  vehicles: Vehicle[]
  onClose: () => void
  onApplied: () => Promise<void> | void
}) {
  const plan = view.plan!
  const [picked, setPicked] = useState<Option | null>(null)
  const [impact, setImpact] = useState<MoveImpact | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const current = view.stops.find((s) => s.order_id === order.id)
  const currentRoute = current ? view.routes.find((r) => r.id === current.route_id) : null
  const inCarrier = plan.carrier.includes(order.id)
  const inPlan = Boolean(current) || inCarrier
  const orderKg = n(order.total_kg) ?? 0
  const deadline = day10(order.carrier_deadline)
  const days = useMemo(() => [...new Set([...plan.days.map((d) => day10(d)), ...(view.assignable_days ?? [])])].filter((d): d is string => Boolean(d)).sort(), [plan.days, view.assignable_days])

  const options = useMemo<Option[]>(() => {
    const out: Option[] = []
    for (const day of days) {
      for (const v of vehicles.filter((x) => x.active !== false)) {
        const r = view.routes.find((x) => x.vehicle_id === v.id && day10(x.day) === day)
        const warnings: string[] = []
        if (order.needs_tail_lift && !v.has_tail_lift) warnings.push("sem plataforma")
        if ((n(r?.kg) ?? 0) + orderKg > (n(v.max_kg) ?? 0)) warnings.push("acima da carga útil")
        if (order.delivery_cost != null && deadline && day > deadline) warnings.push("depois da última hipótese de transportadora")
        out.push({
          key: `${v.id}:${day}`,
          target: { vehicle_id: v.id, day },
          label: `${dayShort(day)} · ${v.name}`,
          sub: r ? `${r.stop_count} paragens · ${fmtKg(r.kg)}` : "não sai (abre a rota)",
          warnings,
          disabled: r?.status === "confirmed" ? "confirmada" : r && currentRoute?.id === r.id ? "está aqui" : null,
        })
      }
    }
    // Cada transportadora com o seu custo; as que não servem ficam à vista com o motivo.
    const cheapest = order.options.filter((o) => o.ok).sort((a, b) => (a.cost ?? Infinity) - (b.cost ?? Infinity))[0]
    for (const c of order.options) {
      const here = inCarrier && c.service_id === order.carrier_service_id
      out.push({
        key: `svc:${c.service_id}`,
        target: { carrier_service_id: c.service_id },
        label: `${c.carrier_name} · ${c.service_name}`,
        sub: c.ok ? `${eur(c.cost)} · chega ${dayShort(c.arrival)}${c.service_id === cheapest?.service_id ? " · a mais barata" : ""}` : (c.why ?? "não serve"),
        warnings: [],
        disabled: !c.ok ? (c.why ?? "não serve") : here ? "está aqui" : null,
      })
    }
    // Retirar do plano: volta a «por atribuir» (nem frota nem transportadora).
    if (current || inCarrier) out.push({ key: "unplanned", target: "unplanned", label: "Retirar do plano", sub: "volta a «por atribuir»", warnings: [], disabled: null })
    return out
  }, [days, vehicles, view.routes, order, orderKg, deadline, currentRoute, inCarrier, current])

  const preview = async (o: Option) => {
    setPicked(o)
    setImpact(null)
    setError(null)
    setBusy(true)
    try {
      setImpact((await moveOrder(shell, order.id, o.target, true)).impact)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const apply = async () => {
    if (!picked) return
    setBusy(true)
    try {
      await moveOrder(shell, order.id, picked.target, false)
      shell.toast("success", `${order.source_ref} ${inPlan ? "movida" : "atribuída"}`, picked.label)
      await onApplied()
      onClose()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const vName = (id: string) => vehicles.find((v) => v.id === id)?.name ?? "—"
  const total = impact ? impact.delta_variable + impact.delta_carrier : 0

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{inPlan ? "Mover" : "Atribuir"} {order.source_ref} · {order.customer_name ?? ""} · {order.locality ?? ""}</DialogTitle>
        </DialogHeader>
        <div className="grid max-h-72 grid-cols-1 gap-1.5 overflow-y-auto sm:grid-cols-2">
          {options.map((o) => (
            <button
              key={o.key}
              disabled={!!o.disabled || busy}
              onClick={() => preview(o)}
              className={cn(
                "flex flex-col items-start rounded-lg border p-2 text-left text-[12.5px] transition-colors hover:border-foreground/30 disabled:cursor-not-allowed disabled:opacity-50",
                picked?.key === o.key && "border-accent ring-1 ring-accent",
              )}
            >
              <b>{o.label}</b>
              <span className="text-muted-foreground">{o.disabled ?? o.sub}</span>
              {o.warnings.length > 0 && <span className="mt-0.5 flex flex-wrap gap-1">{o.warnings.map((w) => <Tag key={w} tone="warn">{w}</Tag>)}</span>}
            </button>
          ))}
        </div>

        {busy && !impact && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> A calcular o impacto…</div>}
        {error && <div className="rounded-lg border border-red-200 bg-red-50 p-2 text-sm text-red-800">{error}</div>}
        {impact && (
          <div className="flex flex-col gap-2 rounded-lg border p-3 text-[12.5px]">
            {impact.routes.map((r) => (
              <div key={`${r.vehicle_id}:${r.day}`}>
                <b>{vName(r.vehicle_id)} · {dayShort(r.day)}</b>:{" "}
                {r.before ? `${r.before.stops} → ${r.after.stops} paragens · ${r.before.km} → ${r.after.km} km · volta ${hhmm(r.before.end_min)} → ${hhmm(r.after.end_min)} · ${eur(r.before.cost_variable)} → ${eur(r.after.cost_variable)}` : `nova rota · ${r.after.stops} paragem · ${r.after.km} km · volta ${hhmm(r.after.end_min)} · ${eur(r.after.cost_variable)}`}
              </div>
            ))}
            <div>
              Frota (variável) <b className="font-mono">{signed(impact.delta_variable)}</b> · transportadora <b className="font-mono">{signed(impact.delta_carrier)}</b> · total{" "}
              <b className={cn("font-mono", total > 0 ? "text-red-700" : "text-green-700")}>{signed(total)}</b>
              {impact.delta_fixed !== 0 && <> · custo fixo {signed(impact.delta_fixed)} ({impact.delta_fixed > 0 ? "o veículo passa a sair" : "o veículo deixa de sair"})</>}
            </div>
            {impact.problems.length > 0 && (
              <ul className="list-disc rounded-md bg-red-50 py-1.5 pl-5 text-red-800">
                {impact.problems.map((p) => <li key={p}>{p}</li>)}
              </ul>
            )}
            {typeof impact.to === "object" && "vehicle_id" in impact.to && <span className="text-muted-foreground">A encomenda fica fixada nessa rota: «Gerar proposta» não a tira de lá.</span>}
            {typeof impact.to === "object" && "carrier_service_id" in impact.to && <span className="text-muted-foreground">Fica esta transportadora: o «Recalcular» não a troca pela mais barata.</span>}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button disabled={!impact || busy} onClick={apply}>
            {busy && impact ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            {impact?.problems.length ? "Aplicar mesmo assim" : "Aplicar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
